import React, { useCallback, useState } from 'react';
import EditPreviewToggle from '../../shared/EditPreviewToggle';
import { ASSISTANT_THREAD_BG } from '../../shared/prompt-display/chatPanelSurfaces';
import { editorSurfacePaddingSx, instructionBodyTextSx, instructionPreviewMarkdownSx } from '../../shared/markdownBodySx';
import { primarySectionHeaderSx } from '../../../designTokens';
import { promptCardHeaderSx, promptCardShellSx, promptContentGlowEmbeddedSx } from '../../../constants/panelSurfaces';
import MarkdownPreviewEditor from '../../shared/MarkdownPreviewEditor';
import {
  Box,
  Typography,
  TextField,
  Menu,
  MenuItem,
  Popover,
  Button,
  ButtonGroup,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  ListItemText,
  SvgIcon,
} from '@mui/material';
import Tooltip from '../../shared/LibraryTooltip';
import {
  AttachFileOutlined as AttachFileOutlinedIcon,
  LanguageOutlined as LanguageOutlinedIcon,
  HubOutlined as IntegrationIcon,
  SendOutlined as SendIcon,
  ExpandMoreOutlined as ExpandMoreIcon,
  FullscreenOutlined as FullscreenIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const KNOWLEDGE_CONTENT_SECTION_HEADER_SX = {
  ...primarySectionHeaderSx,
  fontSize: '1.125rem',
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: 1.4,
};

const KNOWLEDGE_EDITOR_EDIT_SX = {
  ...editorSurfacePaddingSx,
};

const KNOWLEDGE_EDITOR_EMPTY_PLACEHOLDER = 'Add knowledge content…';

const SUPPORTED_FILE_FORMATS_HINT =
  'TXT, DOCX, PDF, Markdown, HTML, CSV, JSON, YAML, and common code/config files.';

/** Google “G” mark (brand colors) for integration UI. */
const GoogleGIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props} aria-hidden>
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </SvgIcon>
);

const BasicInfoTab = ({
  contextData,
  handleContextInputChange,
  handleFileUpload,
  onFileDrop,
  handleContentChange,
  handleFetchWebUrl,
  webUrlImportMode = 'fetch',
  onWebUrlImportModeChange = () => {},
  contextPreview,
  handleGenerateSummary,
  isUploading,
  googleDriveStatus,
  googleDriveLoading,
  googleDriveActionLoading,
  handleConnectGoogleDrive,
  handleDisconnectGoogleDrive,
  topRowExtras,
  inputSize = 'medium',
  initialViewMode = 'edit',
}) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [integrationsDialogOpen, setIntegrationsDialogOpen] = useState(false);
  const [contextExpandOpen, setContextExpandOpen] = useState(false);
  const [webImportMenuAnchor, setWebImportMenuAnchor] = useState(null);
  const [webUrlPopoverAnchor, setWebUrlPopoverAnchor] = useState(null);
  const [dropActive, setDropActive] = useState(false);
  const editorContent = contextData.content || contextPreview || '';
  const hasContextBodyText = Boolean((contextData.content || '').trim());

  const handleViewModeChange = useCallback((nextMode) => {
    setViewMode(nextMode);
  }, []);

  const contextEditorDisabled = false;

  const currentWebImportLabel = webUrlImportMode === 'url_only' ? 'URL only' : 'Fetch';

  const handleCurrentWebImport = async () => {
    const result = await handleFetchWebUrl(webUrlImportMode);
    if (result?.ok !== false) setWebUrlPopoverAnchor(null);
  };

  const handleWebImportChoice = async (mode) => {
    setWebImportMenuAnchor(null);
    onWebUrlImportModeChange(mode);
    const result = await handleFetchWebUrl(mode);
    if (result?.ok !== false) setWebUrlPopoverAnchor(null);
  };

  /** Web URL + send — inline in toolbar to the right of the type selector. */
  const webUrlPopoverContent = (
      <TextField
        size="small"
        hiddenLabel
        placeholder="https://…"
        value={contextData.webUrl || ''}
        onChange={(e) => handleContextInputChange('webUrl', e.target?.value || '')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (contextData.webUrl || '').trim() && !isUploading) {
            e.preventDefault();
            handleCurrentWebImport();
          }
        }}
        aria-label="Web URL"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ mr: 0.25 }}>
              <ButtonGroup
                size="small"
                variant="text"
                disabled={isUploading || !(contextData.webUrl || '').trim()}
                sx={{
                  height: 28,
                  '& .MuiButtonGroup-grouped': {
                    borderColor: 'divider',
                    minWidth: 0
                  }
                }}
              >
                <Tooltip
                  title={
                    isUploading
                      ? 'Working…'
                      : webUrlImportMode === 'url_only'
                        ? 'Add the URL as knowledge text (no fetch)'
                        : 'Fetch page content from the URL'
                  }
                >
                  <span>
                    <Button
                      onClick={handleCurrentWebImport}
                      disabled={isUploading || !(contextData.webUrl || '').trim()}
                      aria-label={webUrlImportMode === 'url_only' ? 'Add URL as knowledge' : 'Fetch URL content'}
                      sx={{
                        minWidth: 58,
                        height: 28,
                        px: 0.75,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        textTransform: 'none'
                      }}
                    >
                      {isUploading ? (
                        <CircularProgress color="inherit" size={14} />
                      ) : (
                        currentWebImportLabel
                      )}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Choose web import mode">
                  <span>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setWebImportMenuAnchor(e.currentTarget);
                    }}
                    disabled={isUploading || !(contextData.webUrl || '').trim()}
                    aria-label="Choose web import mode"
                    sx={{
                      minWidth: 28,
                      height: 28,
                      px: 0.25,
                      color: 'text.secondary',
                    }}
                  >
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                  </Button>
                  </span>
                </Tooltip>
              </ButtonGroup>
              <Menu
                anchorEl={webImportMenuAnchor}
                open={Boolean(webImportMenuAnchor)}
                onClose={() => setWebImportMenuAnchor(null)}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => handleWebImportChoice('fetch')} dense>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <SendIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Fetch" />
                </MenuItem>
                <MenuItem onClick={() => handleWebImportChoice('url_only')} dense>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <LanguageOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="URL only" />
                </MenuItem>
              </Menu>
            </InputAdornment>
          )
        }}
        sx={{
          width: '100%',
          minWidth: 0,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.paper',
            borderRadius: 1,
            '& fieldset': { borderColor: 'divider' },
            fontSize: '0.8125rem',
            minHeight: 34,
            py: 0
          },
          '& .MuiOutlinedInput-input': {
            py: 0.75,
            px: 1
          }
        }}
      />
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {topRowExtras ? (
        <Box sx={{ width: '100%', flexShrink: 0 }}>
          {topRowExtras}
        </Box>
      ) : null}

      {/* Context: toolbar above editor (full width) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width: '100%'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            ...promptCardShellSx,
            borderRadius: '8px',
            width: '100%',
          }}
        >
        <Box
          sx={{
            ...promptCardHeaderSx,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            px: { xs: 1, sm: 2 },
            py: 1.125,
          }}
        >
          <Typography variant="subtitle1" sx={{ ...KNOWLEDGE_CONTENT_SECTION_HEADER_SX, fontSize: { xs: '1rem', sm: '1.125rem' }, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
            Knowledge content
          </Typography>
            <Box data-markdown-clickaway-ignore="true" sx={{ ml: { xs: 0, sm: 'auto' } }}>
              <EditPreviewToggle mode={viewMode} onModeChange={handleViewModeChange} sx={{ '& .MuiTab-root': { px: { xs: 0.5, sm: 1.25 }, fontSize: { xs: '0.75rem', sm: '0.8125rem' } } }} />
            </Box>
            <Tooltip title={`Supported formats: ${SUPPORTED_FILE_FORMATS_HINT}`}>
                <IconButton
                  size="small"
                  component="label"
                  disabled={isUploading}
                  aria-label={isUploading ? 'Uploading file' : 'Attach file'}
                  sx={{ flexShrink: 0 }}
                >
                  {isUploading ? <CircularProgress size={18} color="inherit" /> : <AttachFileOutlinedIcon fontSize="small" />}
                  <input
                    type="file"
                    hidden
                    accept=".txt,.docx,.pdf,.md,.markdown,.html,.htm,.csv,.json,.jsonl,.yaml,.yml,.js,.jsx,.ts,.tsx,.py,.sh,.toml,.xml,.css,.scss,.env,.ini,.conf,.config,.sql,.graphql,.gql,.gitignore,.dockerignore"
                    onChange={handleFileUpload}
                  />
                </IconButton>
            </Tooltip>
            <Tooltip title="Add URL">
              <IconButton
                size="small"
                aria-label="Add URL"
                onClick={(event) => setWebUrlPopoverAnchor(event.currentTarget)}
                sx={{ flexShrink: 0 }}
              >
                <LanguageOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Manage integrations">
              <IconButton
                size="small"
                aria-label="Manage integrations"
                onClick={() => setIntegrationsDialogOpen(true)}
                sx={{ flexShrink: 0 }}
              >
                <IntegrationIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Popover
              open={Boolean(webUrlPopoverAnchor)}
              anchorEl={webUrlPopoverAnchor}
              onClose={() => setWebUrlPopoverAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { p: 1.25, mt: 0.75, width: { xs: 'calc(100vw - 32px)', sm: 420 }, maxWidth: 'calc(100vw - 32px)' } }}
            >
              {webUrlPopoverContent}
            </Popover>
            <Box data-markdown-clickaway-ignore="true" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {hasContextBodyText ? (
                <Tooltip title="Expand to full screen">
                  <IconButton
                    size="small"
                    aria-label="Expand knowledge text"
                    onClick={() => setContextExpandOpen(true)}
                    sx={{
                      display: { xs: 'none', sm: 'inline-flex' },
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%'
            }}
          >
            <Paper
              onDragOver={(event) => {
                if (!onFileDrop || isUploading) return;
                if (!event.dataTransfer?.types?.includes('Files')) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'copy';
                setDropActive(true);
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setDropActive(false);
                }
              }}
              onDrop={async (event) => {
                if (!onFileDrop || isUploading) return;
                event.preventDefault();
                event.stopPropagation();
                setDropActive(false);
                const file = event.dataTransfer?.files?.[0];
                if (!file) return;
                await onFileDrop(file);
              }}
              sx={{
                bgcolor: dropActive ? 'action.hover' : ASSISTANT_THREAD_BG,
                border: dropActive ? '1px dashed' : 0,
                borderColor: dropActive ? 'primary.main' : 'transparent',
                borderRadius: 0,
                width: '100%',
                p: 0,
                overflow: 'hidden',
                boxShadow: 'none',
                transition: 'background-color 0.15s ease, border-color 0.15s ease',
              }}
            >
              <Box
                sx={{
                  ...promptContentGlowEmbeddedSx,
                  minHeight: { xs: 240, sm: 280 },
                  overflow: 'visible',
                  borderRadius: 0,
                }}
              >
                <MarkdownPreviewEditor
                  value={editorContent}
                  onChange={handleContentChange}
                  mode={viewMode}
                  onModeChange={setViewMode}
                  showToggle={false}
                  keepEditorMounted
                  clickAwayToPreview={!contextEditorDisabled}
                  minRows={8}
                  contentTextSx={instructionBodyTextSx}
                  placeholder={KNOWLEDGE_EDITOR_EMPTY_PLACEHOLDER}
                  previewSx={{ '& .markdown-body': instructionPreviewMarkdownSx }}
                  editorSx={KNOWLEDGE_EDITOR_EDIT_SX}
                  renderEditor={() => (
                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      inputProps={{ 'aria-label': 'Knowledge content' }}
                      variant="standard"
                      value={contextData.content || ''}
                      onChange={(e) => handleContentChange(e.target?.value || '')}
                      disabled={contextEditorDisabled}
                      placeholder={KNOWLEDGE_EDITOR_EMPTY_PLACEHOLDER}
                      InputProps={{ disableUnderline: true }}
                      sx={{
                        '& .MuiInputBase-root': { p: 0, bgcolor: 'transparent' },
                        '& .MuiInputBase-input': {
                          ...instructionBodyTextSx,
                          cursor: contextEditorDisabled ? 'not-allowed' : 'text',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'text.disabled',
                          opacity: 1,
                          fontStyle: 'italic',
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={contextExpandOpen}
        onClose={() => setContextExpandOpen(false)}
        maxWidth={false}
        fullWidth
        aria-label="Knowledge text, expanded editor"
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'min(1400px, calc(100% - 48px))' },
            maxWidth: '100%',
            height: { xs: 'min(92vh, calc(100vh - 16px))', sm: 'min(90vh, calc(100vh - 48px))' },
            maxHeight: { xs: '92vh', sm: '90vh' }
          }
        }}
      >
        <Box
          component="header"
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 0.5,
            py: 0.25,
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          <Tooltip title="Close">
            <IconButton
              edge="end"
              aria-label="Close expanded knowledge"
              onClick={() => setContextExpandOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          component="textarea"
          value={contextData.content || ''}
          onChange={(e) => handleContentChange(e.target?.value || '')}
          disabled={contextEditorDisabled}
          placeholder={KNOWLEDGE_EDITOR_EMPTY_PLACEHOLDER}
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            boxSizing: 'border-box',
            border: 'none',
            outline: 'none',
            resize: 'none',
            p: 2,
            ...instructionBodyTextSx,
            bgcolor: 'background.paper',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            WebkitOverflowScrolling: 'touch',
            '&::placeholder': { color: 'text.disabled' },
            '&:disabled': { cursor: 'not-allowed', opacity: 0.6 },
          }}
        />
      </Dialog>

      <Dialog open={integrationsDialogOpen} onClose={() => setIntegrationsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage integrations</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <GoogleGIcon sx={{ fontSize: 22, flexShrink: 0 }} />
            <Typography variant="subtitle2" component="h3" sx={{ m: 0, fontWeight: 600 }}>
              Google Docs
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {!googleDriveStatus?.configured
              ? 'Private Google Docs import is not configured in this environment.'
              : googleDriveStatus?.connected
                ? `Connected as ${googleDriveStatus.email || 'your Google account'}. Disconnect to revoke access or switch accounts.`
                : 'Connect your Google account to import private Google Docs (in addition to public URLs).'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {googleDriveLoading ? (
              <CircularProgress size={20} />
            ) : googleDriveStatus?.configured ? (
              googleDriveStatus?.connected ? (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleDisconnectGoogleDrive}
                  disabled={googleDriveActionLoading}
                  size={inputSize}
                >
                  {googleDriveActionLoading ? <CircularProgress size={18} color="inherit" /> : 'Disconnect Google'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleConnectGoogleDrive}
                  disabled={googleDriveActionLoading}
                  size={inputSize}
                >
                  {googleDriveActionLoading ? <CircularProgress size={18} color="inherit" /> : 'Connect Google Docs'}
                </Button>
              )
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntegrationsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BasicInfoTab;
